import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Globe, Calendar, User, Package } from 'lucide-react';
import ConsignmentEntryFormI from './ConsignmentEntryFormI';
import apiClient from '../services/api';
import '../styles/ConsignmentList.css';

const ConsignmentI = () => {
  const [consignments, setConsignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();

  useEffect(() => {
    loadConsignments();
  }, []);

  const loadConsignments = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await apiClient.get('/ConsignmentsI.php');
      
      if (response.data.success) {
        const consignments = response.data.data.consignments || [];
        setConsignments(consignments);
      } else {
        throw new Error(response.data.message || 'Failed to load');
      }
    } catch (err) {
      console.error('Error loading consignments:', err);
      
      let errorMessage = 'Failed to load consignments';
      
      if (err.response) {
        if (err.response.status === 401) {
          errorMessage = 'Session expired. Please login again.';
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else {
          errorMessage = err.response.data?.message || errorMessage;
        }
      } else if (err.request) {
        errorMessage = 'Server not responding. Please check if backend is running.';
      } else {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setEditData(null);
    setShowForm(true);
  };

  const handleEdit = (consignment) => {
    setEditData(consignment);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
  if (!window.confirm('Are you sure you want to delete this consignment?')) {
    return;
  }

  try {
    const response = await apiClient.delete('/ConsignmentsI.php', {
      data: { ConsignmentID: id }  // ← send ID in body
    });
    
    if (response.data.success) {
      alert('Consignment deleted successfully!');
      loadConsignments();
    } else {
      alert('Error: ' + response.data.message);
    }
  } catch (err) {
    console.error('Error deleting:', err);
    alert('Error deleting consignment: ' + (err.response?.data?.message || err.message));
  }
};

  // ✅ FIXED: POST for create, PUT for edit, use formData.ConsignmentNo
  const handleSave = async (formData) => {
    try {
      const isEdit = formData.ConsignmentID;
      
      let response;
      if (isEdit) {
        // ✅ UPDATE: Use PUT
        response = await apiClient.put(`/ConsignmentsI.php/${formData.ConsignmentID}`, formData);
      } else {
        // ✅ CREATE: Use POST
        response = await apiClient.post('/ConsignmentsI.php', formData);
      }
      
      if (response.data.success) {
        // ✅ FIX: Use formData.ConsignmentNo (always works!)
        alert(isEdit 
          ? `Consignment updated: ${formData.ConsignmentNo}` 
          : `Consignment created: ${formData.ConsignmentNo}`
        );
        setShowForm(false);
        setEditData(null);
        loadConsignments();
      } else {
        throw new Error(response.data.message);
      }
    } catch (err) {
      console.error('Error saving:', err);
      throw new Error(err.response?.data?.message || err.message);
    }
  };

  const handleClose = () => {
    setShowForm(false);
    setEditData(null);
  };

  if (!session?.companyid) {
    return (
      <div className="consignment-page">
        <div className="empty-state">
          <h3>No Session Context</h3>
          <p>Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  const filteredConsignments = consignments.filter(c =>
    c.ConsignmentNo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ShipperName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.ConsigneeName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="consignment-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
            <Globe size={28} />
          </div>
          <div>
            <h1>International Consignments</h1>
            <p className="header-subtitle">Manage international shipments and exports</p>
          </div>
        </div>
        <button className="btn-primary" onClick={handleNew}>
          <Plus size={20} />
          New Consignment
        </button>
      </div>

      {/* Toolbar */}
      <div className="page-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by consignment no, shipper, or consignee..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="toolbar-info">
          <span className="count-badge" style={{ background: '#f59e0b' }}>{filteredConsignments.length}</span>
          {filteredConsignments.length === 1 ? 'consignment' : 'consignments'}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading consignments...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
          <button className="btn-primary" onClick={loadConsignments}>
            Retry
          </button>
        </div>
      ) : filteredConsignments.length === 0 ? (
        <div className="empty-state">
          <Globe size={48} style={{ opacity: 0.3 }} />
          <h3>No consignments found</h3>
          <p>
            {searchTerm 
              ? 'Try adjusting your search terms' 
              : 'Get started by creating your first international consignment'}
          </p>
          {!searchTerm && (
            <button className="btn-primary" onClick={handleNew}>
              <Plus size={20} />
              New Consignment
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Consignment No</th>
                <th>Date</th>
                <th>Shipper</th>
                <th>Consignee</th>
                <th>Country → Country</th>
                <th>Weight (kg)</th>
                <th>Status</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredConsignments.map(consignment => (
                <tr key={consignment.ConsignmentID}>
                  <td className="font-mono">{consignment.ConsignmentNo}</td>
                  <td>
                    <div className="date-cell">
                      <Calendar size={14} />
                      {new Date(consignment.ConsignmentDate).toLocaleDateString()}
                    </div>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <User size={14} />
                      <div>
                        <div className="customer-name">{consignment.ShipperName || '-'}</div>
                        {consignment.ShipperCode && (
                          <div className="customer-code">{consignment.ShipperCode}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="customer-cell">
                      <User size={14} />
                      <div>
                        <div className="customer-name">{consignment.ConsigneeName || '-'}</div>
                        {consignment.ConsigneeCode && (
                          <div className="customer-code">{consignment.ConsigneeCode}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="route-cell">
                      {consignment.ShipperCountry || 'N/A'} → {consignment.ConsigneeCountry || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="weight-cell">
                      <Package size={14} />
                      {consignment.Weight ? parseFloat(consignment.Weight).toFixed(2) : '0.00'}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge status-${consignment.StatusColor || 'default'}`}>
                      {consignment.Status || 'Pending'}
                    </span>
                  </td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(consignment)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(consignment.ConsignmentID)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <ConsignmentEntryFormI
          onClose={handleClose}
          onSave={handleSave}
          editData={editData}
        />
      )}
    </div>
  );
};

export default ConsignmentI;